import React, { useState, useEffect, useRef } from 'react';
import AlertCard from './AlertCard';
import { playAlarmBeeps, stopAlarmBeeps } from '../../utils/alertAudio';

const AlertModal = ({ alerts = [], onClose, playSound = true }) => {
  const [isSoundPlaying, setIsSoundPlaying] = useState(false);
  const [dismissedIds, setDismissedIds] = useState([]);
  const oscillatorsRef = useRef([]);
  const soundTimeoutRef = useRef(null);

  const visibleAlerts = alerts.filter(a => !dismissedIds.includes(a.id));
  const hasCritical = alerts.some(a => a.severity === 'critical');

  // Reset locally dismissed ids when the incoming alerts set changes identity
  useEffect(() => {
    setDismissedIds([]);
  }, [alerts]);

  // Play a single, shared alarm for the whole modal while there is at least
  // one critical alert to show. Stops automatically when the list empties.
  useEffect(() => {
    if (playSound && hasCritical && alerts.length > 0) {
      const timer = setTimeout(() => {
        startAlertSound();
      }, 100);

      return () => clearTimeout(timer);
    }

    stopAlertSound();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playSound, hasCritical, alerts.length]);

  // Cleanup: stop sound when modal unmounts
  useEffect(() => {
    return () => {
      stopAlertSound();
    };
  }, []);

  const startAlertSound = async () => {
    try {
      // Stop anything already playing, but keep the shared AudioContext alive
      // (see alertAudio.js) - closing it would re-lock it, requiring a fresh
      // user gesture before any future alarm could play again.
      stopAlertSound();

      const duration = 15; // Total duration in seconds per cycle
      oscillatorsRef.current = await playAlarmBeeps(duration);
      setIsSoundPlaying(true);

      // Auto-stop after duration
      soundTimeoutRef.current = setTimeout(() => {
        setIsSoundPlaying(false);
      }, duration * 1000);
    } catch (error) {
      console.error("Error playing alert sound:", error);
      setIsSoundPlaying(false);
    }
  };

  const stopAlertSound = () => {
    if (soundTimeoutRef.current) {
      clearTimeout(soundTimeoutRef.current);
      soundTimeoutRef.current = null;
    }

    stopAlarmBeeps(oscillatorsRef.current);
    oscillatorsRef.current = [];
    setIsSoundPlaying(false);
  };

  const toggleSound = () => {
    if (isSoundPlaying) {
      stopAlertSound();
    } else {
      startAlertSound();
    }
  };

  const handleAcknowledged = (alertId) => {
    setDismissedIds(prev => [...prev, alertId]);
  };

  const handleClose = () => {
    stopAlertSound();
    onClose();
  };

  if (!alerts || alerts.length === 0) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 animate-fade-in"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="alert-modal-title"
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col animate-slide-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-200 dark:border-slate-700">
          <div>
            <h2
              id="alert-modal-title"
              className="text-2xl font-bold text-gray-900 dark:text-white"
            >
              {alerts.length > 1 ? `${alerts.length} Alerts` : 'Alert Details'}
            </h2>
            <p className="text-lg font-semibold mt-1 text-danger-600 dark:text-danger-400">
              {alerts.length > 1
                ? 'New critical alerts detected'
                : `${(alerts[0].severity || '').toUpperCase()} - ${(alerts[0].type || '').replace(/_/g, ' ')}`}
            </p>
          </div>

          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            aria-label="Close modal"
          >
            <svg className="w-6 h-6" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content - scrollable list of alerts */}
        <div className="p-6 space-y-3 overflow-y-auto">
          {visibleAlerts.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-gray-400 py-4">
              All alerts acknowledged
            </p>
          ) : (
            visibleAlerts.map(alert => (
              <AlertCard
                key={alert.id}
                alert={alert}
                onAcknowledged={handleAcknowledged}
              />
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-slate-700">
          {/* Sound control button */}
          {playSound && hasCritical ? (
            <button
              onClick={toggleSound}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                isSoundPlaying
                  ? 'bg-danger-100 dark:bg-danger-900/20 text-danger-700 dark:text-danger-400'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
              title={isSoundPlaying ? 'Stop sound' : 'Play sound'}
            >
              {isSoundPlaying ? (
                <>
                  <svg className="w-5 h-5 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Stop Alarm
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
                  </svg>
                  Play Alarm
                </>
              )}
            </button>
          ) : (
            <div></div>
          )}

          <div className="flex items-center gap-3">
            <button
              onClick={handleClose}
              className="btn-secondary"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertModal;
